import React from 'react';
import { Box, Paper, Skeleton, Typography, ToggleButtonGroup, ToggleButton, alpha } from '@mui/material';
import { BarChart, LineChart, PieChart } from '@mui/x-charts';

const PERFORMANCE_ACCENT = '#3388f2';

function readableTextColor(hexColor, fallback = '#f8fafc') {
  const hex = String(hexColor || '').replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return fallback;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.58 ? '#111827' : '#f8fafc';
}

/**
 * Reusable chart card component for dashboards
 * Supports Bar, Line, and Pie charts with consistent styling
 * @param {'default'|'performance'} [variant] — performance: teal top bar, eyebrow, minimal chart chrome (performance dashboard)
 */
const DashboardChartCard = ({
  title,
  subtitle,
  data,
  chartType = 'bar', // 'bar', 'line', 'area', 'pie'
  dataKeys = [],
  colors = [],
  theme = 'light',
  height = 260,
  showToggle = false,
  toggleValue = 'monthly',
  onToggleChange = () => {},
  formatValue = (val) => val.toLocaleString(),
  pieData = null, // For pie charts, pass array of { name, value, color }
  pieInnerRadius = null, // Set for donut style in default variant
  pieShowValueInLabel = false, // Show formatted value in pie labels
  pieAsBars = false, // Render pie data as clear horizontal value bars
  pieBarsDirection = 'ltr', // 'ltr' or 'rtl' fill direction for horizontal value bars
  pieBarExportLimit = null, // Limit pieAsBars items during PDF export
  showArea = false, // For line charts, show filled area under lines
  variant = 'default',
  eyebrow,
  showTopAccent = true,
  showBarTotalTooltip = false,
  barTotalLabel = 'Total',
  barRadius = null,
  loading = false,
}) => {
  const isDark = theme === 'dark';
  const isPerformance = variant === 'performance';
  const isPdfExport = typeof document !== 'undefined' && document.body?.classList?.contains('dashboard-pdf-exporting');
  const themeColors = {
    text: isDark ? '#e7efff' : '#1e293b',
    textSecondary: isDark ? '#d9e3f0' : '#455468',
    grid: isDark ? 'rgba(179,193,214,0.18)' : 'rgba(15,23,42,0.08)',
    background: isDark ? 'rgba(24,27,37,0.96)' : 'rgba(248,251,255,0.96)',
    tooltipBg: isDark ? 'rgba(15,23,42,0.96)' : 'rgba(255, 255, 255, 0.98)',
    tooltipBorder: isDark ? alpha('#93c5fd', 0.28) : alpha(PERFORMANCE_ACCENT, 0.18),
  };
  const formatChartValue = (value) => {
    const formattedValue = String(formatValue(value));
    return formattedValue.replace(/(\d),(\d{2})$/, '$1,$2').replace(/\.00\b/g, '');
  };
  const scrollableChartSx = isPdfExport
    ? {
        height: 'auto',
        maxHeight: 'none',
        overflow: 'visible',
        width: '100%',
      }
    : {
        height,
        maxHeight: height,
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        pr: 0.5,
        boxSizing: 'border-box',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: isDark ? alpha('#ffffff', 0.06) : alpha('#101828', 0.06),
          borderRadius: '999px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: isDark ? alpha('#ffffff', 0.22) : alpha('#101828', 0.22),
          borderRadius: '999px',
          '&:hover': {
            backgroundColor: isDark ? alpha('#ffffff', 0.32) : alpha('#101828', 0.32),
          },
        },
      };
  const measureChartLabel = (label) => {
    if (typeof document === 'undefined') return String(label || '').length * 7;
    const canvas = measureChartLabel.canvas || document.createElement('canvas');
    measureChartLabel.canvas = canvas;
    const context = canvas.getContext('2d');
    context.font = '800 11px Arial, sans-serif';
    return context.measureText(String(label || '')).width;
  };
  const getNiceAxisMax = (value) => {
    if (!Number.isFinite(value) || value <= 0) return 0;
    const exponent = Math.floor(Math.log10(value));
    const magnitude = 10 ** exponent;
    const normalized = value / magnitude;
    const niceNormalized = normalized <= 1
      ? 1
      : normalized <= 2
        ? 2
        : normalized <= 2.5
          ? 2.5
          : normalized <= 5
            ? 5
            : 10;
    return niceNormalized * magnitude;
  };

  const commonChartSx = {
    overflow: 'visible',
    '& svg': {
      overflow: 'visible',
    },
    '& .MuiChartsSurface-root': {
      overflow: 'visible',
    },
    '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
      stroke: isDark ? 'rgba(179,193,214,0.32)' : 'rgba(15,23,42,0.18)',
    },
    '& .MuiChartsGrid-line': {
      stroke: themeColors.grid,
      strokeDasharray: '4 5',
    },
    '& .MuiChartsAxis-tickLabel': {
      fill: themeColors.textSecondary,
      fontWeight: 800,
    },
    '& .MuiChartsLegend-series text': {
      fill: themeColors.text,
      fontWeight: 700,
      fontSize: 13,
    },
    '@media (max-width: 600px)': {
      '& .MuiChartsLegend-series text': {
        fontSize: 10,
        fontWeight: 800,
      },
      '& .MuiChartsLegend-mark': {
        width: 10,
        height: 10,
      },
    },
    '& .MuiBarElement-root': {
      filter: isDark ? 'drop-shadow(0 4px 8px rgba(7,12,20,0.42))' : 'drop-shadow(0 3px 7px rgba(51,136,242,0.18))',
      transition: 'filter .2s ease, opacity .2s ease',
    },
    '& .MuiBarElement-root:hover': {
      filter: isDark ? 'drop-shadow(0 7px 12px rgba(7,12,20,0.55))' : 'drop-shadow(0 6px 10px rgba(51,136,242,0.26))',
    },
    '& .MuiLineElement-root': {
      filter: isDark ? 'drop-shadow(0 4px 8px rgba(7,12,20,0.42))' : 'drop-shadow(0 3px 7px rgba(51,136,242,0.18))',
    },
  };

  const emptyState = (
    <Box
      sx={{
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: themeColors.textSecondary,
        fontSize: '0.875rem',
        fontWeight: 700,
      }}
    >
      No data to display for this period.
    </Box>
  );

  const renderChart = () => {
    if (chartType === 'pie') {
      if (!pieData || !pieData.length) return emptyState;
      const pieTotalValue = pieData.reduce((sum, item) => sum + (Number(item?.value) || 0), 0);
      if (pieAsBars) {
        const barsData = isPdfExport && pieBarExportLimit
          ? [...pieData]
            .sort((left, right) => Number(right.value || 0) - Number(left.value || 0))
            .slice(0, pieBarExportLimit)
          : pieData;
        return (
          <Box
            sx={{
              ...scrollableChartSx,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              px: { xs: 1, sm: 1.4 },
              py: { xs: 1, sm: 1.4 },
            }}
          >
            {barsData.map((entry, index) => {
              const value = Number(entry.value || 0);
              const percent = pieTotalValue > 0 ? Math.round((value / pieTotalValue) * 100) : 0;
              const color = entry.color || colors[index % colors.length] || PERFORMANCE_ACCENT;
              const label = entry.name || entry.label || 'Value';
              return (
                <Box key={`${label}-${index}`}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 0.5,
                      mb: 0.7,
                    }}
                  >
                    <Typography
                      sx={{
                        color: themeColors.text,
                        fontWeight: 900,
                        fontSize: { xs: '0.85rem', sm: '0.96rem' },
                        minWidth: 0,
                        flex: '1 1 120px',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        lineHeight: 1.25,
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        color: themeColors.text,
                        fontWeight: 900,
                        fontSize: { xs: '0.78rem', sm: '0.92rem' },
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        ml: 'auto',
                      }}
                    >
                      {formatChartValue(value)} ({percent}%)
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      height: 18,
                      borderRadius: 999,
                      overflow: 'hidden',
                      bgcolor: isDark ? alpha('#ffffff', 0.1) : alpha('#101828', 0.08),
                      border: `1px solid ${isDark ? alpha('#ffffff', 0.1) : alpha('#101828', 0.08)}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.max(percent, value > 0 ? 2 : 0)}%`,
                        height: '100%',
                        marginLeft: pieBarsDirection === 'rtl' ? 'auto' : 0,
                        borderRadius: 999,
                        background: `linear-gradient(90deg, ${alpha(color, 0.82)} 0%, ${color} 100%)`,
                        boxShadow: isDark ? `0 0 14px ${alpha(color, 0.28)}` : `0 6px 14px ${alpha(color, 0.2)}`,
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      }
      const pieOuter = Math.min(height * (isPerformance ? 0.38 : 0.35), isPerformance ? 125 : 120);
      const pieLabelColors = {};
      return (
        <Box sx={{ position: 'relative', minHeight: height }}>
          <PieChart
            height={height}
            margin={{ top: 12, right: 8, bottom: 12, left: 8 }}
            series={[
              {
                data: pieData.map((entry, index) => ({
                  id: entry.name || entry.label || index,
                  label: entry.name || entry.label || 'Value',
                  value: Number(entry.value || 0),
                  color: entry.color || colors[index % colors.length],
                })).map((entry) => {
                  pieLabelColors[entry.id] = readableTextColor(entry.color, isDark ? '#f8fafc' : '#1a2433');
                  return entry;
                }),
                innerRadius: typeof pieInnerRadius === 'number' ? pieInnerRadius : Math.round(pieOuter * 0.55),
                outerRadius: pieOuter,
                cornerRadius: isPerformance ? 7 : 4,
                paddingAngle: isPerformance ? 2 : 0,
                arcLabel: (item) => {
                  if (!item.value || pieTotalValue <= 0) return '';
                  const pct = (item.value / pieTotalValue) * 100;
                  if (pct < 0.5) return '';
                  return `${formatChartValue(item.value)} (${pct.toFixed(0)}%)`;
                },
                arcLabelMinAngle: 14,
                valueFormatter: (item) => `${item.label}: ${formatChartValue(item.value)}`,
              },
            ]}
            slotProps={{ legend: { hidden: false } }}
          sx={{
            ...commonChartSx,
            '& .MuiPieArcLabel-root': {
              fontWeight: 900,
              fontSize: 10.5,
              paintOrder: 'stroke',
              stroke: isDark ? 'rgba(15,23,42,0.72)' : 'rgba(255,255,255,0.78)',
              strokeWidth: 2.8,
            },
            ...Object.entries(pieLabelColors).reduce((acc, [id, color]) => {
              acc[`& .MuiPieArcLabel-root[data-id="${id}"]`] = { fill: color };
              return acc;
            }, {}),
            '& .MuiPieArc-root': {
              transition: 'transform .2s ease, filter .2s ease',
              transformOrigin: 'center center',
              filter: isDark ? 'drop-shadow(0 3px 8px rgba(7,12,20,0.45))' : 'drop-shadow(0 3px 6px rgba(51,136,242,0.18))',
            },
            '& .MuiPieArc-root:hover': {
              filter: isDark ? 'drop-shadow(0 6px 12px rgba(7,12,20,0.58))' : 'drop-shadow(0 6px 10px rgba(51,136,242,0.28))',
            },
          }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              pointerEvents: 'none',
              color: themeColors.text,
              lineHeight: 1.05,
            }}
          >
            <Typography sx={{ fontSize: 10, fontWeight: 900, color: themeColors.textSecondary }}>
              Total
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 900, color: themeColors.text }}>
              {formatChartValue(pieTotalValue)}
            </Typography>
          </Box>
        </Box>
      );
    }

    const chartSeries = dataKeys.map((key, index) => {
      const dataKey = typeof key === 'string' ? key : key.dataKey;
      const color = typeof key === 'string' ? colors[index % colors.length] : (key.color || colors[index % colors.length]);
      const name = typeof key === 'string' ? key : (key.name || key.dataKey);
      return {
        dataKey,
        label: name,
        color,
        stack: typeof key === 'string' ? undefined : key.stackId,
        yAxisKey: typeof key === 'string' ? undefined : key.yAxisKey,
        area: showArea || chartType === 'area',
        curve: isPerformance ? 'natural' : 'linear',
        valueFormatter: (value) => formatChartValue(value),
      };
    });

    const usesDualAxis = chartType === 'bar'
      && chartSeries.some((series) => series.yAxisKey === 'left' || series.yAxisKey === 'right');

    const collectAxisValues = (axisKey) => {
      const values = [];
      (data || []).forEach((row) => {
        dataKeys.forEach((key) => {
          const dataKey = typeof key === 'string' ? key : key.dataKey;
          const keyAxis = typeof key === 'string' ? 'left' : (key.yAxisKey || 'left');
          if (!usesDualAxis || keyAxis === axisKey) {
            values.push(Number(row?.[dataKey] || 0));
          }
        });
      });
      return values;
    };

    const buildAxisConfig = (axisKey, position = 'left') => {
      const axisValues = collectAxisValues(axisKey);
      const maxAxisValue = Math.max(0, ...axisValues.map((value) => Math.abs(Number(value) || 0)));
      const niceAxisMax = getNiceAxisMax(maxAxisValue);
      const labelCandidates = [
        0,
        maxAxisValue,
        niceAxisMax,
        niceAxisMax * 0.25,
        niceAxisMax * 0.5,
        niceAxisMax * 0.75,
      ].map((value) => formatChartValue(value));
      const widestAxisLabel = Math.max(...labelCandidates.map(measureChartLabel));
      const axisWidth = Math.min(
        isPdfExport ? 132 : 100,
        Math.max(isPerformance ? 40 : 36, widestAxisLabel + (isPdfExport ? 34 : 24))
      );
      return {
        id: axisKey,
        position,
        width: axisWidth,
        tickLabelStyle: { fill: themeColors.textSecondary, fontSize: 11, fontWeight: 700 },
        valueFormatter: formatChartValue,
      };
    };

    const yAxisValues = collectAxisValues('left');
    if (!usesDualAxis) {
      (data || []).forEach((row) => {
        const stackTotals = {};
        dataKeys.forEach((key) => {
          const dataKey = typeof key === 'string' ? key : key.dataKey;
          if (key?.stackId) {
            stackTotals[key.stackId] = (stackTotals[key.stackId] || 0) + Number(row?.[dataKey] || 0);
          }
        });
        yAxisValues.push(...Object.values(stackTotals));
      });
    }
    const maxYAxisValue = Math.max(0, ...yAxisValues.map((value) => Math.abs(Number(value) || 0)));
    // Pad above peak so line/area markers and bar tops are not clipped at the chart edge.
    const niceAxisMax = getNiceAxisMax(maxYAxisValue > 0 ? maxYAxisValue * 1.18 : 0);
    const labelCandidates = [
      0,
      maxYAxisValue,
      niceAxisMax,
      niceAxisMax * 0.25,
      niceAxisMax * 0.5,
      niceAxisMax * 0.75,
    ].map((value) => formatChartValue(value));
    const widestYAxisLabel = Math.max(...labelCandidates.map(measureChartLabel));
    const dynamicYAxisWidth = Math.min(
      isPdfExport ? 132 : 100,
      Math.max(isPerformance ? 40 : 36, widestYAxisLabel + (isPdfExport ? 34 : 24))
    );
    const leftAxisConfig = usesDualAxis ? buildAxisConfig('left', 'left') : null;
    const rightAxisConfig = usesDualAxis ? buildAxisConfig('right', 'right') : null;
    const yAxisLabelWidth = usesDualAxis ? leftAxisConfig.width : dynamicYAxisWidth;
    const chartLeftMargin = yAxisLabelWidth + (isPdfExport ? 18 : 10);
    const chartRightMargin = usesDualAxis
      ? rightAxisConfig.width + (isPdfExport ? 28 : 18)
      : (isPdfExport ? 34 : 16);
    const singleYAxisConfig = {
      width: yAxisLabelWidth,
      min: 0,
      ...(niceAxisMax > 0 ? { max: niceAxisMax } : {}),
      tickLabelStyle: { fill: themeColors.textSecondary, fontSize: 11, fontWeight: 700 },
      valueFormatter: formatChartValue,
    };

    if (chartType === 'line' || chartType === 'area') {
      return (
        <LineChart
          dataset={data || []}
          height={height}
          margin={{ top: 28, right: chartRightMargin, left: chartLeftMargin, bottom: isPerformance ? 96 : 20 }}
          xAxis={[
            {
              scaleType: 'point',
              dataKey: 'name',
              tickLabelStyle: {
                fill: themeColors.textSecondary,
                fontSize: data?.length > 20 ? 9 : 11,
                fontWeight: 800,
              },
            },
          ]}
          yAxis={[singleYAxisConfig]}
          series={chartSeries}
          slotProps={{
            legend: {
              position: { vertical: 'bottom', horizontal: 'middle' },
              direction: 'row',
              itemGap: isPerformance ? 12 : 16,
              markGap: 5,
              labelStyle: {
                fontSize: isPerformance ? 11 : 12,
                fontWeight: 800,
              },
            },
          }}
          grid={{ horizontal: true, vertical: false }}
          sx={{
            ...commonChartSx,
            '& .MuiChartsSurface-root': { overflow: 'visible' },
          }}
        />
      );
    }

    return (
      <BarChart
        dataset={data || []}
        height={height}
        margin={{ top: 20, right: chartRightMargin, left: chartLeftMargin, bottom: isPerformance ? 104 : 24 }}
        xAxis={[
          {
            scaleType: 'band',
            dataKey: 'name',
            categoryGapRatio: isPerformance ? 0.22 : 0.32,
            tickLabelStyle: {
              fill: themeColors.textSecondary,
              fontSize: data?.length > 8 ? 9 : (data?.length > 20 ? 9 : 11),
              fontWeight: 800,
            },
          },
        ]}
        yAxis={
          usesDualAxis
            ? [leftAxisConfig, rightAxisConfig]
            : [singleYAxisConfig]
        }
        series={chartSeries.map((series) => ({
          ...series,
          yAxisKey: usesDualAxis ? (series.yAxisKey || 'left') : undefined,
          borderRadius: Array.isArray(barRadius) ? 0 : (isPerformance ? 8 : 4),
        }))}
        slotProps={{
          legend: {
            position: { vertical: 'bottom', horizontal: 'middle' },
            direction: 'row',
            itemGap: isPerformance ? 12 : 16,
            markGap: 5,
            labelStyle: {
              fontSize: isPerformance ? 11 : 12,
              fontWeight: 800,
            },
          },
        }}
        grid={{ horizontal: true, vertical: false }}
        sx={commonChartSx}
      />
    );
  };

  const paperSx = isPerformance
    ? {
        p: 0,
        overflow: isPdfExport ? 'visible' : 'hidden',
        position: 'relative',
        borderRadius: 3,
        width: '100%',
        height: isPdfExport && pieAsBars ? 'auto' : '100%',
        boxSizing: 'border-box',
        border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : alpha(PERFORMANCE_ACCENT, 0.2)}`,
        background: isDark
          ? 'linear-gradient(160deg, rgba(27,29,39,0.98) 0%, rgba(24,27,37,0.96) 62%, rgba(22,29,43,0.92) 100%)'
          : 'linear-gradient(160deg, #ffffff 0%, #f8fbff 58%, #edf6ff 100%)',
        boxShadow: isDark
          ? `0 14px 34px ${alpha('#000', 0.16)}`
          : `0 18px 42px ${alpha(PERFORMANCE_ACCENT, 0.13)}`,
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      }
    : {
        p: 3,
        backgroundColor: isDark ? '#2a2a31' : '#ffffff',
        border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
        borderRadius: 2,
        height: '100%',
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      };

  return (
    <Paper elevation={0} sx={paperSx}>
      {isPerformance && (
        <>
          <Box
            sx={{
              position: 'absolute',
              top: -90,
              right: -70,
              width: 230,
              height: 230,
              borderRadius: '50%',
              bgcolor: alpha(PERFORMANCE_ACCENT, isDark ? 0.07 : 0.12),
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: -70,
              bottom: -95,
              width: 210,
              height: 210,
              borderRadius: '50%',
              bgcolor: alpha('#18c6d8', isDark ? 0.06 : 0.1),
              pointerEvents: 'none',
            }}
          />
        </>
      )}
      <Box sx={{ p: isPerformance ? 2.5 : 0, position: 'relative', zIndex: 1 }}>
        <Box sx={{ mb: 2 }}>
        {isPerformance && eyebrow && (
          <Typography
            variant="overline"
            sx={{
              color: PERFORMANCE_ACCENT,
              fontWeight: 800,
              letterSpacing: '0.14em',
              display: 'block',
              lineHeight: 1.2,
              mb: title || subtitle ? 0.75 : 0,
            }}
          >
            {eyebrow}
          </Typography>
        )}
        {title ? (
          <Typography
            variant="h6"
            sx={{
              fontWeight: isPerformance ? 800 : 600,
              letterSpacing: isPerformance ? '-0.02em' : undefined,
              color: themeColors.text,
              mb: subtitle ? 0.5 : 0,
              transition: 'color 0.3s ease',
            }}
          >
            {title}
          </Typography>
        ) : null}
        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              color: themeColors.textSecondary,
              fontSize: '0.875rem',
              lineHeight: 1.5,
              transition: 'color 0.3s ease',
              ...(isPerformance && !title ? { mt: 0 } : {}),
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          width: '100%',
          ...(pieAsBars
            ? {
                overflow: isPdfExport ? 'visible' : 'hidden',
                ...(isPdfExport
                  ? { height: 'auto', maxHeight: 'none', minHeight: 0 }
                  : {}),
              }
            : { minHeight: { xs: height + 34, sm: height } }),
          ...(isPerformance
            ? {
                mt: 0.5,
                px: isPdfExport ? { xs: 2.2, sm: 3 } : { xs: 1, sm: 1.4 },
                py: pieAsBars ? 0 : { xs: 1, sm: 1.4 },
                overflow: isPdfExport ? 'visible' : 'hidden',
                borderRadius: 2.4,
                border: `1px solid ${isDark ? alpha('#ffffff', 0.07) : alpha(PERFORMANCE_ACCENT, 0.16)}`,
                background: isDark
                  ? 'linear-gradient(160deg, rgba(20,22,30,0.88) 0%, rgba(23,25,34,0.92) 100%)'
                  : 'linear-gradient(160deg, rgba(255,255,255,0.94) 0%, rgba(248,251,255,0.96) 100%)',
              }
            : {}),
        }}
      >
        {loading ? (
          <Skeleton
            variant="rounded"
            height={height}
            sx={{
              borderRadius: 2,
              bgcolor: isDark ? alpha(PERFORMANCE_ACCENT, 0.1) : alpha(PERFORMANCE_ACCENT, 0.08),
            }}
          />
        ) : (
          renderChart()
        )}
      </Box>

      {showToggle && (
        <>
          <Box sx={{ borderTop: `1px solid ${themeColors.grid}`, mt: 3, pt: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <ToggleButtonGroup
              value={toggleValue}
              exclusive
              onChange={onToggleChange}
              aria-label="view mode"
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  px: 2,
                  py: 0.5,
                  color: themeColors.text,
                  borderColor: themeColors.grid,
                  '&.Mui-selected': {
                    backgroundColor: isDark ? '#333246' : '#e0e0e0',
                    color: themeColors.text,
                    '&:hover': {
                      backgroundColor: isDark ? '#444350' : '#d0d0d0',
                    },
                  },
                  '&:hover': {
                    backgroundColor: isDark ? '#2a2a31' : '#f5f5f5',
                  },
                },
              }}
            >
              <ToggleButton value="yearly" aria-label="yearly">
                Yearly
              </ToggleButton>
              <ToggleButton value="monthly" aria-label="monthly">
                Monthly
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </>
      )}
      </Box>
    </Paper>
  );
};

export default DashboardChartCard;
